// SSE wire-format reader for POST streams read through `fetch`.
//
// We use the SSE *framing* (`event:` / `data:` / blank-line terminator) but not
// the `EventSource` *API*: EventSource is GET-only and cannot set headers, and
// both our streams are POSTs that need a JSON body (and, for Ask Sophie, an
// Authorization header). This loop was previously inlined in
// applyStreamingSpeakerLabels (MedicalScribeTranscriptionData.js); it is lifted
// here so the Ask Sophie stream reuses the dialect that is already proven
// against a real GCP endpoint instead of inventing a second one.
//
// Payload semantics deliberately stay with the caller: this yields the raw
// `data` string, because the two consumers disagree about it (speaker labels
// send bare JSON strings and a literal `[DONE]` sentinel; Ask Sophie sends JSON
// objects and a terminal `done` event).

// One `\n\n`-terminated frame -> {event, data}. Comment lines (`:` prefix, used
// for the anti-buffering padding and the 10s heartbeats) carry no dispatch and
// give null. Per the SSE spec, repeated `data:` lines join with a newline and a
// single space after the colon is optional.
function parseFrame(frame) {
  let event = null;
  const dataLines = [];
  for (const line of frame.split(/\r?\n/)) {
    if (!line || line.startsWith(':')) continue;
    if (line.startsWith('event:')) {
      event = line.slice(6).replace(/^ /, '');
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).replace(/^ /, ''));
    }
  }
  if (event === null && dataLines.length === 0) return null;
  return { event: event || 'message', data: dataLines.join('\n') };
}

// Yields {event, data} per complete frame. A trailing partial frame at end of
// stream is dropped on purpose: an unterminated frame is by definition
// incomplete, and for Ask Sophie a half-arrived terminal event must read as a
// truncation, never as a completion.
export async function* readSSE(response) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop(); // keep the incomplete trailing frame
      for (const frame of frames) {
        const parsed = parseFrame(frame);
        if (parsed) yield parsed;
      }
    }
  } finally {
    // Reached on an early `break` by the consumer too (e.g. Ask Sophie stopping
    // at its terminal event), so the connection is not left hanging.
    if (typeof reader.cancel === 'function') {
      try {
        await reader.cancel();
      } catch (e) {
        /* the stream is already gone; nothing to release */
      }
    }
  }
}
